import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { 
  Atom, Upload, Download, Paperclip, FileText, Image, FileSpreadsheet, X, Brain, 
  ChevronDown, Wrench, MessageSquare, Plus, Trash2, ChevronLeft, ChevronRight, Edit3, 
  Check, MoreVertical, Copy, Share2, Mic, MicOff, ThumbsUp, ThumbsDown, ArrowDown,
  Search, Pin, PinOff, Folder, Sparkles, Clock, Keyboard, Trash, Link, FileDown
} from 'lucide-react';
import { useUserPreferences } from '../context/UserPreferencesContext';
import './AnalysisPage.css';

// API Configuration - uses your new backend
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const AnalysisPage = () => {
  const { t, formatPrice, preferences, updatePreference } = useUserPreferences();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [selectedTools, setSelectedTools] = useState([]);
  
  // Chat History State
  const [chatHistory, setChatHistory] = useState(() => {
    const saved = localStorage.getItem('intheoria-chat-history');
    return saved ? JSON.parse(saved) : [];
  });
  const [currentChatId, setCurrentChatId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  
  // New Feature States
  const [searchQuery, setSearchQuery] = useState('');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(null);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const toolsMenuRef = useRef(null);
  const importMenuRef = useRef(null);
  const inputRef = useRef(null);

  // Suggestions data for autocomplete
  const suggestionsData = {
    stocks: [
      { symbol: 'AAPL', name: 'Apple Inc', type: 'stock', sector: 'Technology' },
      { symbol: 'NVDA', name: 'NVIDIA Corp', type: 'stock', sector: 'Technology' },
      { symbol: 'TSLA', name: 'Tesla Inc', type: 'stock', sector: 'Automotive' },
      { symbol: 'MSFT', name: 'Microsoft Corp', type: 'stock', sector: 'Technology' },
      { symbol: 'GOOGL', name: 'Alphabet Inc', type: 'stock', sector: 'Technology' },
      { symbol: 'AMZN', name: 'Amazon.com', type: 'stock', sector: 'Consumer' },
      { symbol: 'META', name: 'Meta Platforms', type: 'stock', sector: 'Technology' },
      { symbol: 'JPM', name: 'JPMorgan Chase', type: 'stock', sector: 'Financials' },
      { symbol: 'LMT', name: 'Lockheed Martin', type: 'stock', sector: 'Defense' },
      { symbol: 'RTX', name: 'RTX Corp', type: 'stock', sector: 'Defense' },
      { symbol: 'BA', name: 'Boeing Co', type: 'stock', sector: 'Defense' },
      { symbol: 'UNH', name: 'UnitedHealth', type: 'stock', sector: 'Healthcare' },
      { symbol: 'PFE', name: 'Pfizer Inc', type: 'stock', sector: 'Healthcare' },
      { symbol: 'XOM', name: 'Exxon Mobil', type: 'stock', sector: 'Energy' },
    ],
    politicians: [
      { name: 'Nancy Pelosi', party: 'D', chamber: 'House', type: 'politician' },
      { name: 'Michael McCaul', party: 'R', chamber: 'House', type: 'politician' },
      { name: 'Tommy Tuberville', party: 'R', chamber: 'Senate', type: 'politician' },
      { name: 'Marjorie Taylor Greene', party: 'R', chamber: 'House', type: 'politician' },
      { name: 'Ro Khanna', party: 'D', chamber: 'House', type: 'politician' },
      { name: 'Dan Crenshaw', party: 'R', chamber: 'House', type: 'politician' },
      { name: 'Josh Gottheimer', party: 'D', chamber: 'House', type: 'politician' },
      { name: 'Alexandria Ocasio-Cortez', party: 'D', chamber: 'House', type: 'politician' },
      { name: 'Mark Warner', party: 'D', chamber: 'Senate', type: 'politician' },
      { name: 'Rick Scott', party: 'R', chamber: 'Senate', type: 'politician' },
    ],
    commands: [
      { label: 'Analyze', description: 'Get full stock analysis', type: 'command', action: 'Analyze ' },
      { label: 'Compare', description: 'Compare two stocks', type: 'command', action: 'Compare ' },
      { label: 'Price of', description: 'Quick price lookup', type: 'command', action: 'Price of ' },
      { label: 'News for', description: 'Latest news', type: 'command', action: 'News for ' },
      { label: 'Insider trades', description: 'Insider trading activity', type: 'command', action: 'Insider trades for ' },
      { label: 'Congress trades', description: 'Congressional trading', type: 'command', action: 'Congress trades for ' },
      { label: 'Market Overview', description: 'Market summary', type: 'command', action: 'Market Overview' },
      { label: 'Economic indicators', description: 'Economic data', type: 'command', action: 'Economic indicators' },
    ]
  };

  // Filter suggestions based on input
  const filteredSuggestions = useMemo(() => {
    console.log('Computing suggestions for:', inputValue);
    if (!inputValue || inputValue.trim().length === 0) return [];
    
    const query = inputValue.toLowerCase().trim();
    const results = [];
    
    // Always show commands for short queries
    if (query.length <= 4 || query.startsWith('/')) {
      const cmdQuery = query.startsWith('/') ? query.slice(1) : query;
      suggestionsData.commands.forEach(cmd => {
        if (cmd.label.toLowerCase().includes(cmdQuery) || 
            cmd.description.toLowerCase().includes(cmdQuery) ||
            cmdQuery.length <= 2) {
          results.push(cmd);
        }
      });
    }
    
    // Search stocks
    suggestionsData.stocks.forEach(stock => {
      if (stock.symbol.toLowerCase().includes(query) || 
          stock.name.toLowerCase().includes(query) ||
          stock.sector.toLowerCase().includes(query)) {
        results.push(stock);
      }
    });
    
    // Search politicians
    suggestionsData.politicians.forEach(pol => {
      if (pol.name.toLowerCase().includes(query) ||
          pol.party.toLowerCase() === query ||
          pol.chamber.toLowerCase().includes(query)) {
        results.push(pol);
      }
    });
    
    console.log('Found suggestions:', results.length, results);
    return results.slice(0, 8);
  }, [inputValue]);

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'stock') {
      setInputValue(`Analyze ${suggestion.symbol}`);
    } else if (suggestion.type === 'politician') {
      setInputValue(`Congress trades for ${suggestion.name}`);
    } else if (suggestion.type === 'command') {
      setInputValue(suggestion.action);
    }
    setShowSuggestions(false);
    setActiveSuggestionIndex(0);
    inputRef.current?.focus();
  };

  const handleInputKeyDown = (e) => {
    if (showSuggestions && filteredSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestionIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
      } else if (e.key === 'Tab') {
        e.preventDefault();
        handleSuggestionClick(filteredSuggestions[activeSuggestionIndex]);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      } else if (e.key === 'Enter' && !e.shiftKey) {
        setShowSuggestions(false);
        handleSubmit(e);
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      handleSubmit(e);
    }
  };

  // Scroll handling
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      setShowScrollButton(scrollHeight - scrollTop - clientHeight > 100);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Save chat history to localStorage
  useEffect(() => {
    localStorage.setItem('intheoria-chat-history', JSON.stringify(chatHistory));
  }, [chatHistory]);
  
  // Auto-save current chat when messages change
  useEffect(() => {
    if (messages.length > 0 && currentChatId) {
      setChatHistory(prev => prev.map(chat => 
        chat.id === currentChatId 
          ? { ...chat, messages, updatedAt: new Date().toISOString() }
          : chat
      ));
    }
  }, [messages, currentChatId]);

  // Click outside to close menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target)) {
        setShowToolsMenu(false);
      }
      if (importMenuRef.current && !importMenuRef.current.contains(event.target)) {
        setShowImportMenu(false);
      }
      // Close chat menu dropdown when clicking outside
      if (openMenuId && !event.target.closest('.chat-menu-wrapper')) {
        setOpenMenuId(null);
        setShowShareMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);
  
  // Chat History Functions
  const createNewChat = () => {
    // Save current chat if it has messages
    if (messages.length > 0 && !currentChatId) {
      const newChat = {
        id: Date.now().toString(),
        title: getFirstMessageTitle(messages),
        messages: messages,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setChatHistory(prev => [newChat, ...prev]);
    }
    
    // Start fresh
    setMessages([]);
    setCurrentChatId(null);
    setSelectedTools([]);
    setAttachedFiles([]);
  };
  
  const getFirstMessageTitle = (msgs) => {
    const firstUserMsg = msgs.find(m => m.role === 'user');
    if (firstUserMsg) {
      const content = firstUserMsg.content || '';
      return content.length > 30 ? content.substring(0, 30) + '...' : content || 'New Chat';
    }
    return 'New Chat';
  };
  
  const loadChat = (chatId) => {
    // Save current chat first if needed
    if (messages.length > 0 && !currentChatId) {
      const newChat = {
        id: Date.now().toString(),
        title: getFirstMessageTitle(messages),
        messages: messages,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setChatHistory(prev => [newChat, ...prev]);
    }
    
    const chat = chatHistory.find(c => c.id === chatId);
    if (chat) {
      setMessages(chat.messages);
      setCurrentChatId(chatId);
      setSelectedTools([]);
      setAttachedFiles([]);
    }
  };
  
  const deleteChat = (chatId, e) => {
    e.stopPropagation();
    setChatHistory(prev => prev.filter(c => c.id !== chatId));
    if (currentChatId === chatId) {
      setMessages([]);
      setCurrentChatId(null);
    }
  };
  
  const startEditingChat = (chatId, title, e) => {
    e.stopPropagation();
    setEditingChatId(chatId);
    setEditingTitle(title);
  };
  
  const saveEditingChat = (e) => {
    e.stopPropagation();
    if (editingTitle.trim()) {
      setChatHistory(prev => prev.map(chat =>
        chat.id === editingChatId
          ? { ...chat, title: editingTitle.trim() }
          : chat
      ));
    }
    setEditingChatId(null);
    setEditingTitle('');
  };
  
  const formatChatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };
  
  // Format message timestamp
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Copy message to clipboard
  const copyMessage = async (content, messageId) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  // Pin/Unpin chat
  const togglePinChat = (chatId, e) => {
    e.stopPropagation();
    setChatHistory(prev => prev.map(chat =>
      chat.id === chatId ? { ...chat, pinned: !chat.pinned } : chat
    ));
    setOpenMenuId(null);
  };
  
  // Share chat
  const shareChat = async (chatId, format = 'link') => {
    const chat = chatHistory.find(c => c.id === chatId);
    if (!chat) return;
    
    if (format === 'link') {
      const shareData = {
        title: `InTheoria: ${chat.title}`,
        text: chat.messages.map(m => `${m.role}: ${m.content}`).join('\n\n'),
      };
      
      if (navigator.share) {
        try {
          await navigator.share(shareData);
        } catch (err) {
          await navigator.clipboard.writeText(shareData.text);
          alert('Chat copied to clipboard!');
        }
      } else {
        await navigator.clipboard.writeText(shareData.text);
        alert('Chat copied to clipboard!');
      }
    } else if (format === 'pdf') {
      const content = chat.messages.map(m => 
        `[${m.role.toUpperCase()}] ${formatMessageTime(m.timestamp)}\n${m.content}\n`
      ).join('\n---\n\n');
      
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${chat.title.replace(/[^a-z0-9]/gi, '_')}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    setShowShareMenu(null);
  };
  
  // Clear current conversation
  const clearConversation = () => {
    if (messages.length === 0) return;
    if (window.confirm('Clear current conversation? This will not delete it from history.')) {
      setMessages([]);
      setCurrentChatId(null);
    }
  };
  
  // Voice input
  const toggleVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice input is not supported in this browser.');
      return;
    }
    
    if (isRecording) {
      setIsRecording(false);
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    
    recognition.onstart = () => setIsRecording(true);
    recognition.onend = () => setIsRecording(false);
    recognition.onerror = () => setIsRecording(false);
    
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInputValue(prev => prev + (prev ? ' ' : '') + transcript);
    };
    
    recognition.start();
  };
  
  // Message reactions
  const reactToMessage = (messageIndex, reaction) => {
    setMessages(prev => prev.map((msg, i) => 
      i === messageIndex 
        ? { ...msg, reaction: msg.reaction === reaction ? null : reaction }
        : msg
    ));
  };
  
  // Keyboard shortcuts
  const handleKeyboardShortcuts = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
      e.preventDefault();
      createNewChat();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === '/') {
      e.preventDefault();
      inputRef.current?.focus();
    }
    if (e.key === 'Escape') {
      setShowToolsMenu(false);
      setShowImportMenu(false);
      setOpenMenuId(null);
      setShowShareMenu(null);
      setShowKeyboardShortcuts(false);
      setIsSidebarOpen(false);
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
      e.preventDefault();
      setIsSidebarOpen(prev => !prev);
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      setShowKeyboardShortcuts(prev => !prev);
    }
  }, []);
  
  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardShortcuts);
    return () => document.removeEventListener('keydown', handleKeyboardShortcuts);
  }, [handleKeyboardShortcuts]);
  
  // Fuzzy match helper
  const fuzzyMatch = (text, query) => {
    if (!text || !query) return { match: false, score: 0 };
    
    const textLower = text.toLowerCase();
    const queryLower = query.toLowerCase();
    
    if (textLower.includes(queryLower)) {
      return { match: true, score: 100 };
    }
    
    let queryIndex = 0;
    let score = 0;
    let consecutiveBonus = 0;
    
    for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
      if (textLower[i] === queryLower[queryIndex]) {
        score += 10 + consecutiveBonus;
        consecutiveBonus += 5;
        queryIndex++;
      } else {
        consecutiveBonus = 0;
      }
    }
    
    if (queryIndex === queryLower.length) {
      return { match: true, score };
    }
    
    if (queryLower.length > 2) {
      const words = textLower.split(/\s+/);
      for (const word of words) {
        if (word.length >= queryLower.length - 1) {
          let differences = 0;
          const minLen = Math.min(word.length, queryLower.length);
          for (let i = 0; i < minLen; i++) {
            if (word[i] !== queryLower[i]) differences++;
          }
          differences += Math.abs(word.length - queryLower.length);
          if (differences <= 1) {
            return { match: true, score: 30 };
          }
        }
      }
    }
    
    return { match: false, score: 0 };
  };
  
  // Filter and score chats by search
  const filteredChats = chatHistory
    .map(chat => {
      if (!searchQuery) {
        return { chat, score: 0, isMatch: true };
      }
      
      const titleMatch = fuzzyMatch(chat.title, searchQuery);
      
      let messageScore = 0;
      if (chat.messages) {
        for (const msg of chat.messages) {
          const msgMatch = fuzzyMatch(msg.content, searchQuery);
          if (msgMatch.score > messageScore) {
            messageScore = msgMatch.score;
          }
        }
      }
      
      const bestScore = Math.max(titleMatch.score, messageScore);
      const isMatch = titleMatch.match || messageScore > 0;
      
      return { chat, score: bestScore, isMatch };
    })
    .filter(item => item.isMatch)
    .sort((a, b) => {
      if (a.chat.pinned && !b.chat.pinned) return -1;
      if (!a.chat.pinned && b.chat.pinned) return 1;
      
      if (searchQuery) {
        if (b.score !== a.score) return b.score - a.score;
      }
      
      return new Date(b.chat.updatedAt) - new Date(a.chat.updatedAt);
    })
    .map(item => item.chat);
  
  // Generate follow-up suggestions
  const getFollowUpSuggestions = () => {
    if (messages.length === 0) return [];
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    if (!lastAssistant || lastAssistant.type === 'error') return [];
    
    const suggestions = [];
    const content = lastAssistant.content?.toLowerCase() || '';
    
    if (content.includes('price') || content.includes('quote')) {
      suggestions.push('Show me the technical analysis');
      suggestions.push('What are the key fundamentals?');
    }
    if (content.includes('technical') || content.includes('rsi') || content.includes('macd')) {
      suggestions.push('What does the fundamental data show?');
      suggestions.push('Compare with a competitor');
    }
    if (content.includes('earnings') || content.includes('revenue')) {
      suggestions.push('Show recent news');
      suggestions.push('What about insider trading?');
    }
    if (content.includes('sector') || content.includes('industry')) {
      suggestions.push('Show top performers');
      suggestions.push('Screen for value stocks');
    }
    
    if (suggestions.length === 0) {
      suggestions.push('Tell me more');
      suggestions.push('Show related news');
    }
    
    return suggestions.slice(0, 3);
  };

  // Check backend connection on mount
  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stocks/search?q=test`);
      if (response.ok) {
        setIsConnected(true);
      }
    } catch (error) {
      setIsConnected(false);
    }
  };

  // Handle file import
  const handleFileImport = (type) => {
    setShowImportMenu(false);
    if (fileInputRef.current) {
      fileInputRef.current.accept = type === 'image' ? 'image/*' : 
                                     type === 'spreadsheet' ? '.csv,.xlsx,.xls' : 
                                     type === 'document' ? '.pdf,.doc,.docx,.txt' : '*';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      files.forEach(file => {
        const fileData = {
          name: file.name,
          type: file.type,
          size: file.size,
          file: file
        };
        
        const reader = new FileReader();
        
        reader.onload = (event) => {
          const content = event.target.result;
          fileData.content = content;
          
          if (file.name.endsWith('.csv')) {
            try {
              const lines = content.split('\n');
              const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
              const data = lines.slice(1).filter(line => line.trim()).map(line => {
                const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
                const row = {};
                headers.forEach((header, i) => {
                  row[header] = values[i];
                });
                return row;
              });
              fileData.parsedData = data;
              fileData.headers = headers;
            } catch (err) {
              console.error('Error parsing CSV:', err);
            }
          }
          
          setAttachedFiles(prev => [...prev, fileData]);
        };
        
        if (file.type.startsWith('text/') || 
            file.name.endsWith('.csv') || 
            file.name.endsWith('.txt')) {
          reader.readAsText(file);
        } else if (file.type.startsWith('image/')) {
          reader.readAsDataURL(file);
        } else {
          setAttachedFiles(prev => [...prev, fileData]);
        }
      });
    }
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle export
  const handleExport = (format) => {
    setShowImportMenu(false);
    const exportData = {
      timestamp: new Date().toISOString(),
      messages: messages,
      format: format
    };
    
    let content, filename, mimeType;
    
    if (format === 'json') {
      content = JSON.stringify(exportData, null, 2);
      filename = `intheoria-analysis-${Date.now()}.json`;
      mimeType = 'application/json';
    } else if (format === 'csv') {
      const csvRows = messages.map(m => `"${m.role}","${(m.content || '').replace(/"/g, '""')}","${m.timestamp || ''}"`);
      content = 'Role,Content,Timestamp\n' + csvRows.join('\n');
      filename = `intheoria-analysis-${Date.now()}.csv`;
      mimeType = 'text/csv';
    } else if (format === 'txt') {
      content = messages.map(m => `[${m.role.toUpperCase()}]\n${m.content || ''}\n`).join('\n---\n\n');
      filename = `intheoria-analysis-${Date.now()}.txt`;
      mimeType = 'text/plain';
    }
    
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Get regional quick actions
  const getQuickActions = () => {
    const regionalStocks = {
      US: ['AAPL', 'NVDA', 'TSLA', 'RIVN'],
      EU: ['ASML', 'SAP', 'LVMH', 'NVO'],
      UK: ['SHEL', 'AZN', 'HSBA', 'BP'],
      DE: ['SAP', 'SIE', 'ALV', 'BMW'],
      FR: ['LVMH', 'TTE', 'AIR', 'SAN'],
      JP: ['7203.T', '6758.T', '9984.T', '6861.T'],
      CN: ['BABA', 'TCEHY', 'JD', 'PDD'],
      HK: ['0700.HK', '9988.HK', '0005.HK', '1299.HK'],
      CA: ['SHOP', 'TD', 'RY', 'ENB'],
      AU: ['BHP', 'CBA', 'CSL', 'WBC'],
      IN: ['RELIANCE.NS', 'TCS.NS', 'INFY.NS', 'HDFCBANK.NS'],
      BR: ['VALE', 'PBR', 'ITUB', 'NU'],
      KR: ['005930.KS', '000660.KS', '035420.KS', '051910.KS'],
      GLOBAL: ['AAPL', 'NVDA', 'TSLA', 'ASML'],
    };
    const stocks = regionalStocks[preferences.region] || regionalStocks.US;
    return [
      { label: `${t('analyze')} ${stocks[0]}`, action: `Analyze ${stocks[0]}` },
      { label: `${t('analyze')} ${stocks[1]}`, action: `Analyze ${stocks[1]}` },
      { label: `Compare ${stocks[2]} vs ${stocks[3]}`, action: `Compare ${stocks[2]} vs ${stocks[3]}` },
      { label: t('marketOverview') || 'Market Overview', action: 'Market Overview' },
    ];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((!inputValue.trim() && selectedTools.length === 0 && attachedFiles.length === 0) || isLoading) return;

    const toolCommands = {
      'quote': 'get quote for',
      'technical': 'technical analysis of',
      'fundamental': 'fundamental analysis of',
      'compare': 'compare',
      'news': 'news for',
      'earnings': 'earnings for',
      'insider': 'insider trading for',
      'risk': 'risk metrics for',
      'sector': 'sector analysis',
      'screen': 'screen stocks',
      'economic': 'economic summary',
    };

    let fullMessage = inputValue.trim();
    if (selectedTools.length > 0) {
      const toolPrefixes = selectedTools.map(t => toolCommands[t]).join(', ');
      fullMessage = selectedTools.length > 0 && inputValue.trim() 
        ? `${toolPrefixes} ${inputValue.trim()}`
        : toolPrefixes;
    }

    const userMessage = fullMessage;
    const displayMessage = inputValue.trim() || (attachedFiles.length > 0 ? `Attached ${attachedFiles.length} file(s)` : '');
    const usedTools = [...selectedTools];
    const sentAttachments = [...attachedFiles];
    
    if (messages.length === 0 && !currentChatId) {
      const newChatId = Date.now().toString();
      const newChat = {
        id: newChatId,
        title: displayMessage.length > 30 ? displayMessage.substring(0, 30) + '...' : displayMessage || 'New Chat',
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setChatHistory(prev => [newChat, ...prev]);
      setCurrentChatId(newChatId);
    }
    
    setInputValue('');
    setAttachedFiles([]);
    
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: displayMessage,
      tools: usedTools,
      attachments: sentAttachments,
      timestamp: new Date().toISOString(),
      id: Date.now().toString()
    }]);
    setIsLoading(true);

    try {
      const response = await processQuery(userMessage);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.content,
        data: response.data,
        type: response.type,
        timestamp: new Date().toISOString(),
        id: (Date.now() + 1).toString()
      }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Error: ${error.message}. Please check if the backend server is running on port 3001.`,
        type: 'error',
        timestamp: new Date().toISOString(),
        id: (Date.now() + 1).toString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const processQuery = async (query) => {
    const lowerQuery = query.toLowerCase();
    
    const analyzeMatch = lowerQuery.match(/(?:analyze|analysis|look at|check|tell me about|what about|how is|how's)\s+([a-zA-Z0-9.]{1,10})/i);
    const compareMatch = lowerQuery.match(/compare\s+([a-zA-Z0-9.]{1,10})\s+(?:vs?|versus|and|to|with)\s+([a-zA-Z0-9.]{1,10})/i);
    const quoteMatch = lowerQuery.match(/(?:price|quote|stock price|current price)\s+(?:of|for)?\s*([a-zA-Z0-9.]{1,10})/i);
    
    if (compareMatch) {
      const [, symbol1, symbol2] = compareMatch;
      return await compareStocks(symbol1.toUpperCase(), symbol2.toUpperCase());
    }
    
    if (analyzeMatch) {
      const symbol = analyzeMatch[1].toUpperCase();
      return await analyzeStock(symbol);
    }
    
    if (quoteMatch) {
      const symbol = quoteMatch[1].toUpperCase();
      return await getQuote(symbol);
    }
    
    const tickerMatch = query.match(/\b([A-Z0-9.]{1,10})\b/);
    if (tickerMatch && tickerMatch[1].length >= 2) {
      return await analyzeStock(tickerMatch[1]);
    }
    
    return {
      content: `I can help you analyze stocks, compare companies, or get market insights. Try asking me to:\n\n• **${t('analyze')} AAPL** - Get comprehensive stock analysis\n• **Compare TSLA vs RIVN** - Side-by-side comparison\n• **${t('price')} of NVDA** - Quick quote lookup\n• **${t('marketOverview') || 'Market overview'}** - Economic indicators`,
      type: 'info'
    };
  };

  // ============================================
  // FIXED API CALLS - Using new backend endpoints
  // ============================================

  const analyzeStock = async (symbol) => {
    try {
      // Use the new /api/stocks/overview endpoint
      const response = await fetch(`${API_URL}/api/stocks/overview/${symbol}`);
      
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const data = await response.json();
      
      // Also get quote data
      const quoteResponse = await fetch(`${API_URL}/api/stocks/quote/${symbol}`);
      const quoteData = quoteResponse.ok ? await quoteResponse.json() : {};
      
      return {
        content: formatAnalysisText(symbol, { ...data, quote: quoteData }),
        data: { company: data, quote: quoteData },
        type: 'analysis',
        ticker: symbol
      };
    } catch (error) {
      // Fallback to just quote
      return await getQuote(symbol);
    }
  };

  const getQuote = async (symbol) => {
    try {
      const response = await fetch(`${API_URL}/api/stocks/quote/${symbol}`);
      
      if (!response.ok) throw new Error('Failed to fetch quote');
      
      const data = await response.json();
      return {
        content: formatQuoteText(symbol, data),
        data: { quote: data },
        type: 'analysis',
        ticker: symbol
      };
    } catch (error) {
      throw new Error(`Could not fetch data for ${symbol}`);
    }
  };

  const compareStocks = async (symbol1, symbol2) => {
    try {
      // Get data for both stocks
      const [quote1, quote2, overview1, overview2] = await Promise.all([
        fetch(`${API_URL}/api/stocks/quote/${symbol1}`).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/stocks/quote/${symbol2}`).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/stocks/overview/${symbol1}`).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/stocks/overview/${symbol2}`).then(r => r.json()).catch(() => ({}))
      ]);
      
      const data = {
        [symbol1]: { quote: quote1, overview: overview1 },
        [symbol2]: { quote: quote2, overview: overview2 }
      };
      
      return {
        content: formatComparison(symbol1, symbol2, data),
        data: data,
        type: 'comparison'
      };
    } catch (error) {
      throw new Error(`Could not compare ${symbol1} and ${symbol2}`);
    }
  };

  const formatAnalysisText = (symbol, data) => {
    if (!data) return `Unable to fetch analysis for ${symbol}`;
    
    const quote = data.quote || {};
    const company = data.company || data;
    
    return `## ${symbol} ${t('analysis')}

**${company.Name || company.name || symbol}** | ${company.Sector || company.sector || 'N/A'} | ${company.Industry || company.industry || 'N/A'}

### ${t('currentPrice') || 'Current Price'}
**${formatPrice(quote.price)}** ${quote.changePercent >= 0 ? '📈' : '📉'} ${quote.changePercent?.toFixed(2) || 0}%

### Key Metrics
• **${t('marketCap')}:** ${formatMarketCap(quote.marketCap || company.MarketCapitalization)}
• **P/E Ratio:** ${company.PERatio || quote.pe || 'N/A'}
• **52W ${t('high')}:** ${formatPrice(company['52WeekHigh'] || quote.yearHigh)}
• **52W ${t('low')}:** ${formatPrice(company['52WeekLow'] || quote.yearLow)}
• **${t('volume')}:** ${formatVolumeText(quote.volume)}

${company.Description ? `### About\n${company.Description.substring(0, 300)}...` : ''}`;
  };

  const formatQuoteText = (symbol, data) => {
    return `## ${symbol} Quote

**${formatPrice(data.price)}** ${data.changePercent >= 0 ? '📈' : '📉'} ${data.changePercent?.toFixed(2) || 0}% ${t('today').toLowerCase()}

• **${t('open')}:** ${formatPrice(data.open)}
• **${t('high')}:** ${formatPrice(data.high)}
• **${t('low')}:** ${formatPrice(data.low)}
• **${t('volume')}:** ${formatVolumeText(data.volume)}`;
  };

  const formatComparison = (symbol1, symbol2, data) => {
    const s1 = data[symbol1] || {};
    const s2 = data[symbol2] || {};
    const q1 = s1.quote || {};
    const q2 = s2.quote || {};
    
    return `## ${symbol1} vs ${symbol2} Comparison

| Metric | ${symbol1} | ${symbol2} |
|--------|-----------|-----------|
| **Price** | ${formatPrice(q1.price)} | ${formatPrice(q2.price)} |
| **Change** | ${q1.changePercent?.toFixed(2) || 0}% | ${q2.changePercent?.toFixed(2) || 0}% |
| **Market Cap** | ${formatMarketCap(q1.marketCap)} | ${formatMarketCap(q2.marketCap)} |
| **Volume** | ${formatVolumeText(q1.volume)} | ${formatVolumeText(q2.volume)} |`;
  };

  const formatMarketCap = (value) => {
    if (!value) return 'N/A';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (num >= 1e12) return `${formatPrice(num / 1e12).replace(/\.00$/, '')}T`;
    if (num >= 1e9) return `${formatPrice(num / 1e9).replace(/\.00$/, '')}B`;
    if (num >= 1e6) return `${formatPrice(num / 1e6).replace(/\.00$/, '')}M`;
    return formatPrice(num);
  };

  const formatVolumeText = (value) => {
    if (!value) return 'N/A';
    if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
    return value.toLocaleString();
  };

  const formatMessage = (content) => {
    return content
      .split('\n')
      .map((line, i) => {
        if (line.startsWith('## ')) {
          return <h3 key={i} className="msg-h2">{line.substring(3)}</h3>;
        }
        if (line.startsWith('### ')) {
          return <h4 key={i} className="msg-h3">{line.substring(4)}</h4>;
        }
        line = line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
        if (line.startsWith('• ')) {
          return <div key={i} className="msg-list-item" dangerouslySetInnerHTML={{ __html: line }} />;
        }
        if (line.startsWith('|')) {
          return <div key={i} className="msg-table-row" dangerouslySetInnerHTML={{ __html: line }} />;
        }
        if (line.trim()) {
          return <p key={i} dangerouslySetInnerHTML={{ __html: line }} />;
        }
        return <br key={i} />;
      });
  };

  const handleQuickAction = (action) => {
    setInputValue(action);
    setTimeout(() => {
      const form = document.querySelector('.chat-input-form');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true }));
    }, 100);
  };

  const quickActions = getQuickActions();
  const followUpSuggestions = getFollowUpSuggestions();

  return (
    <div className={`analysis-page ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Keyboard Shortcuts Modal */}
      {showKeyboardShortcuts && (
        <div className="shortcuts-overlay" onClick={() => setShowKeyboardShortcuts(false)}>
          <div className="shortcuts-modal" onClick={(e) => e.stopPropagation()}>
            <div className="shortcuts-header">
              <Keyboard size={20} />
              <h3>Keyboard Shortcuts</h3>
              <button onClick={() => setShowKeyboardShortcuts(false)}><X size={18} /></button>
            </div>
            <div className="shortcuts-list">
              <div className="shortcut-item"><kbd>Ctrl</kbd> + <kbd>N</kbd> <span>New chat</span></div>
              <div className="shortcut-item"><kbd>Ctrl</kbd> + <kbd>/</kbd> <span>Focus input</span></div>
              <div className="shortcut-item"><kbd>Ctrl</kbd> + <kbd>B</kbd> <span>Toggle sidebar</span></div>
              <div className="shortcut-item"><kbd>Ctrl</kbd> + <kbd>K</kbd> <span>Show shortcuts</span></div>
              <div className="shortcut-item"><kbd>Esc</kbd> <span>Close menus</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Chat History Sidebar */}
      <div className={`chat-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h3>Chats</h3>
          <div className="sidebar-header-actions">
            <button className="sidebar-icon-btn" onClick={() => setShowKeyboardShortcuts(true)} title="Keyboard shortcuts">
              <Keyboard size={16} />
            </button>
            <button className="new-chat-btn" onClick={createNewChat} title="New Chat (Ctrl+N)">
              <Plus size={16} />
            </button>
            <button className="sidebar-close-btn" onClick={() => setIsSidebarOpen(false)} title="Close (Esc)">
              <X size={16} />
            </button>
          </div>
        </div>
        
        {/* Search Chats */}
        <div className="sidebar-search">
          <Search size={14} />
          <input 
            type="text"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => setSearchQuery('')}>
              <X size={12} />
            </button>
          )}
        </div>
        
        <div className="chat-list">
          {filteredChats.length === 0 ? (
            <div className="no-chats">
              <MessageSquare size={24} />
              <p>{searchQuery ? 'No chats found' : 'No saved chats yet'}</p>
            </div>
          ) : (
            filteredChats.map(chat => (
              <div 
                key={chat.id}
                className={`chat-item ${currentChatId === chat.id ? 'active' : ''} ${chat.pinned ? 'pinned' : ''}`}
                onClick={() => loadChat(chat.id)}
              >
                <div className="chat-item-content">
                  {chat.pinned && <Pin size={12} className="pin-icon" />}
                  <MessageSquare size={14} />
                  {editingChatId === chat.id ? (
                    <input
                      type="text"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveEditingChat(e);
                        if (e.key === 'Escape') { setEditingChatId(null); setEditingTitle(''); }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="chat-title-input"
                      autoFocus
                    />
                  ) : (
                    <div className="chat-item-info">
                      <span className="chat-title">{chat.title}</span>
                      <span className="chat-date">{formatChatDate(chat.updatedAt)}</span>
                    </div>
                  )}
                </div>
                
                {/* Three-dot menu */}
                <div className="chat-menu-wrapper">
                  <button 
                    className="chat-menu-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === chat.id ? null : chat.id);
                      setShowShareMenu(null);
                    }}
                  >
                    <MoreVertical size={14} />
                  </button>
                  
                  {openMenuId === chat.id && (
                    <div className="chat-menu-dropdown" onClick={(e) => e.stopPropagation()}>
                      <button 
                        className="chat-menu-item"
                        onClick={(e) => togglePinChat(chat.id, e)}
                      >
                        {chat.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                        {chat.pinned ? 'Unpin' : 'Pin'}
                      </button>
                      <button 
                        className="chat-menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowShareMenu(showShareMenu === chat.id ? null : chat.id);
                        }}
                      >
                        <Share2 size={14} />
                        Share
                      </button>
                      {showShareMenu === chat.id && (
                        <div className="share-submenu">
                          <button onClick={() => shareChat(chat.id, 'link')}>
                            <Link size={12} /> Copy link
                          </button>
                          <button onClick={() => shareChat(chat.id, 'pdf')}>
                            <FileDown size={12} /> Download
                          </button>
                        </div>
                      )}
                      <button 
                        className="chat-menu-item"
                        onClick={(e) => {
                          startEditingChat(chat.id, chat.title, e);
                          setOpenMenuId(null);
                        }}
                      >
                        <Edit3 size={14} />
                        Rename
                      </button>
                      <button 
                        className="chat-menu-item delete"
                        onClick={(e) => {
                          deleteChat(chat.id, e);
                          setOpenMenuId(null);
                        }}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Chat History Toggle Button */}
      {!isSidebarOpen && (
        <button 
          className="chat-history-toggle"
          onClick={() => setIsSidebarOpen(true)}
          title="Open chat history (Ctrl+B)"
        >
          <MessageSquare size={18} />
          <span>History</span>
        </button>
      )}

      {/* Connection Status */}
      <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
        <span className="status-dot"></span>
        {isConnected ? t('connected') : t('loading') || 'Connecting...'}
      </div>

      {/* Chat Messages Area */}
      <div className="chat-messages" ref={messagesContainerRef} onScroll={handleScroll}>
        {messages.length === 0 ? (
          <div className="welcome-screen">
            <div className="welcome-content">
              <div className="intheoria-logo">
                <div className="logo-icon">
                  <Brain size={48} strokeWidth={1.5} />
                </div>
                <div className="logo-text">
                  <span className="logo-in">In</span>
                  <span className="logo-theoria">Theoria</span>
                </div>
              </div>
              <p className="welcome-tagline">Deep Understanding. Clear Action.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="chat-header-mini">
              <div className="intheoria-logo-mini">
                <div className="logo-icon">
                  <Brain size={32} strokeWidth={1.5} />
                </div>
                <div className="logo-text-mini">
                  <span className="logo-in">In</span>
                  <span className="logo-theoria">Theoria</span>
                </div>
              </div>
            </div>
            
            {messages.map((message, index) => (
              <div key={message.id || index} className={`chat-message ${message.role}`}>
                <div className="message-avatar">
                  {message.role === 'user' ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  ) : (
                    <Atom size={20} strokeWidth={1.5} />
                  )}
                </div>
                <div className="message-content">
                  <div className="message-header">
                    <span className="message-role">{message.role === 'user' ? 'You' : 'InTheoria'}</span>
                    <span className="message-time">{formatMessageTime(message.timestamp)}</span>
                    {message.role === 'user' && message.tools && message.tools.length > 0 && (
                      <div className="tools-used-wrapper">
                        <button 
                          className="tools-used-btn"
                          onClick={() => {
                            setMessages(prev => prev.map((msg, i) => 
                              i === index ? { ...msg, toolsExpanded: !msg.toolsExpanded } : msg
                            ));
                          }}
                        >
                          <Wrench size={12} />
                          <span>{message.tools.length} tool{message.tools.length > 1 ? 's' : ''}</span>
                        </button>
                        
                        {message.toolsExpanded && (
                          <div className="tools-popup">
                            <div className="tools-popup-header">
                              <span>Tools Used</span>
                              <button 
                                className="tools-popup-close"
                                onClick={() => {
                                  setMessages(prev => prev.map((msg, i) => 
                                  i === index ? { ...msg, toolsExpanded: false } : msg
                                ));
                              }}
                            >
                              <X size={14} />
                            </button>
                          </div>
                          <div className="tools-popup-list">
                            {message.tools.map((toolId) => {
                              const toolLabels = {
                                'quote': 'Get Quote',
                                'technical': 'Technical Analysis',
                                'fundamental': 'Fundamental Analysis',
                                'compare': 'Compare Stocks',
                                'news': 'Latest News',
                                'earnings': 'Earnings Calendar',
                                'insider': 'Insider Trading',
                                'risk': 'Risk Metrics',
                                'sector': 'Sector Analysis',
                                'screen': 'Stock Screener',
                                'economic': 'Economic Data',
                              };
                              return (
                                <div key={toolId} className="tool-popup-item">
                                  <Wrench size={12} />
                                  {toolLabels[toolId]}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {message.role === 'user' && message.attachments && message.attachments.length > 0 && (
                  <div className="message-attachments">
                    {message.attachments.map((attachment, idx) => (
                      <div key={idx} className="attachment-preview">
                        {attachment.type?.startsWith('image/') ? (
                          <>
                            <Image size={14} />
                            <span className="attachment-name">{attachment.name}</span>
                            {attachment.content && (
                              <img 
                                src={attachment.content} 
                                alt={attachment.name}
                                className="attachment-image-thumb"
                              />
                            )}
                          </>
                        ) : attachment.name?.match(/\.(csv|xlsx|xls)$/i) ? (
                          <>
                            <FileSpreadsheet size={14} />
                            <span className="attachment-name">{attachment.name}</span>
                            {attachment.parsedData && (
                              <span className="attachment-meta">{attachment.parsedData.length} rows</span>
                            )}
                          </>
                        ) : (
                          <>
                            <FileText size={14} />
                            <span className="attachment-name">{attachment.name}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                <div className={`message-text ${message.type === 'error' ? 'error' : ''}`}>
                  {formatMessage(message.content)}
                </div>
                
                {message.type === 'analysis' && message.data?.quote && (
                  <div className="analysis-cards">
                    <div className="analysis-card">
                      <h4>{message.ticker}</h4>
                      <div className="quote-data">
                        <span className="price">{formatPrice(message.data.quote.price)}</span>
                        <span className={`change ${message.data.quote.changePercent >= 0 ? 'positive' : 'negative'}`}>
                          {message.data.quote.changePercent >= 0 ? '+' : ''}{message.data.quote.changePercent?.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="message-actions">
                  <button 
                    className={`action-btn ${copiedMessageId === message.id ? 'copied' : ''}`}
                    onClick={() => copyMessage(message.content, message.id)}
                    title="Copy message"
                  >
                    {copiedMessageId === message.id ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                  {message.role === 'assistant' && (
                    <>
                      <button 
                        className={`action-btn ${message.reaction === 'up' ? 'active' : ''}`}
                        onClick={() => reactToMessage(index, 'up')}
                        title="Good response"
                      >
                        <ThumbsUp size={14} />
                      </button>
                      <button 
                        className={`action-btn ${message.reaction === 'down' ? 'active' : ''}`}
                        onClick={() => reactToMessage(index, 'down')}
                        title="Bad response"
                      >
                        <ThumbsDown size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          </>
        )}
        
        {isLoading && (
          <div className="chat-message assistant">
            <div className="message-avatar">
              <Brain size={20} strokeWidth={1.5} />
            </div>
            <div className="message-content">
              <div className="loading-indicator">
                <span>{t('loading') || 'Analyzing'}</span>
                <span className="loading-dots">
                  <span>.</span><span>.</span><span>.</span>
                </span>
              </div>
            </div>
          </div>
        )}
        
        {showScrollButton && (
          <button className="scroll-to-bottom" onClick={scrollToBottom}>
            <ArrowDown size={18} />
          </button>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="chat-input-area">
        {messages.length === 0 && (
          <div className="quick-start-suggestions">
            <Sparkles size={14} />
            <span>Try asking:</span>
            <button 
              className="suggestion-chip"
              onClick={() => {
                setInputValue('Analyze AAPL');
                inputRef.current?.focus();
              }}
            >
              Analyze AAPL
            </button>
            <button 
              className="suggestion-chip"
              onClick={() => {
                setInputValue('Compare TSLA vs RIVN');
                inputRef.current?.focus();
              }}
            >
              Compare TSLA vs RIVN
            </button>
            <button 
              className="suggestion-chip"
              onClick={() => {
                setInputValue('Market Overview');
                inputRef.current?.focus();
              }}
            >
              Market Overview
            </button>
            <button 
              className="suggestion-chip"
              onClick={() => {
                setInputValue('Economic indicators');
                inputRef.current?.focus();
              }}
            >
              Economic indicators
            </button>
          </div>
        )}
        
        {attachedFiles.length > 0 && (
          <div className="attached-files-bar">
            {attachedFiles.map((file, index) => (
              <div key={index} className="attached-file">
                {file.type?.startsWith('image/') ? (
                  <Image size={14} />
                ) : file.name?.match(/\.(csv|xlsx|xls)$/i) ? (
                  <FileSpreadsheet size={14} />
                ) : (
                  <FileText size={14} />
                )}
                <span className="file-name">{file.name}</span>
                <button 
                  className="file-remove"
                  onClick={() => removeAttachment(index)}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        
        <div className="chat-input-box">
          <div className="input-left-actions">
            <div className="attachment-wrapper" ref={importMenuRef}>
              <button 
                className="input-icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowImportMenu(!showImportMenu);
                  setShowToolsMenu(false);
                }}
                title="Import / Export"
              >
                <Paperclip size={20} />
              </button>
              {showImportMenu && (
                <div className="attachment-menu" onClick={(e) => e.stopPropagation()}>
                  <label className="attachment-option">
                    <Upload size={16} />
                    <span>Import</span>
                    <input type="file" accept=".txt,.pdf,.doc,.docx,.csv,.xlsx,.xls,image/*" onChange={(e) => {
                      handleFileChange(e);
                      setShowImportMenu(false);
                    }} hidden />
                  </label>
                  <div className="attachment-submenu">
                    <div className="attachment-option export-parent" disabled={messages.length === 0}>
                      <Download size={16} />
                      <span>Export</span>
                      <ChevronDown size={14} className="submenu-arrow" />
                    </div>
                    {messages.length > 0 && (
                      <div className="export-options">
                        <button className="export-sub-option" onClick={() => handleExport('json')}>
                          JSON
                        </button>
                        <button className="export-sub-option" onClick={() => handleExport('csv')}>
                          CSV
                        </button>
                        <button className="export-sub-option" onClick={() => handleExport('txt')}>
                          Text
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="tools-wrapper" ref={toolsMenuRef}>
              <button 
                className={`input-icon-btn ${selectedTools.length > 0 ? 'has-selection' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowToolsMenu(!showToolsMenu);
                  setShowImportMenu(false);
                }}
                title="Analysis Tools"
              >
                <Wrench size={20} />
              </button>
              {showToolsMenu && (
                <div className="tools-menu">
                  <div className="tools-menu-header">
                    <span>Analysis Tools</span>
                    {selectedTools.length > 0 && (
                      <button 
                        className="clear-tools-btn"
                        onClick={() => setSelectedTools([])}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="tools-list">
                    {[
                      { id: 'quote', label: 'Get Quote' },
                      { id: 'technical', label: 'Technical Analysis' },
                      { id: 'fundamental', label: 'Fundamental Analysis' },
                      { id: 'compare', label: 'Compare Stocks' },
                      { id: 'news', label: 'Latest News' },
                      { id: 'earnings', label: 'Earnings Calendar' },
                      { id: 'insider', label: 'Insider Trading' },
                      { id: 'risk', label: 'Risk Metrics' },
                      { id: 'sector', label: 'Sector Analysis' },
                      { id: 'screen', label: 'Stock Screener' },
                      { id: 'economic', label: 'Economic Data' },
                    ].map((tool) => (
                      <button
                        key={tool.id}
                        className={`tool-option ${selectedTools.includes(tool.id) ? 'selected' : ''}`}
                        onClick={() => {
                          if (selectedTools.includes(tool.id)) {
                            setSelectedTools(selectedTools.filter(t => t !== tool.id));
                          } else {
                            setSelectedTools([...selectedTools, tool.id]);
                          }
                        }}
                      >
                        <span className="tool-checkbox">
                          {selectedTools.includes(tool.id) && '✓'}
                        </span>
                        {tool.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="input-main">
            <div className="input-with-suggestions">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setInputValue(newValue);
                  setShowSuggestions(newValue.length > 0);
                  setActiveSuggestionIndex(0);
                }}
                onKeyDown={handleInputKeyDown}
                onFocus={() => inputValue.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder={selectedTools.length > 0 ? "Enter ticker symbol..." : "Search stocks, politicians, or ask anything..."}
                className="chat-input"
                disabled={isLoading}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              
              {/* Suggestions Dropdown - TODO: Fix positioning to appear above input */}
              {/* Logic preserved in: showSuggestions, filteredSuggestions, handleSuggestionClick, suggestionsData */}
            </div>
            
            {selectedTools.length > 0 && (
              <div className="input-chips-row">
                {selectedTools.map((toolId) => {
                  const toolLabels = {
                    'quote': 'Quote',
                    'technical': 'Technical',
                    'fundamental': 'Fundamental',
                    'compare': 'Compare',
                    'news': 'News',
                    'earnings': 'Earnings',
                    'insider': 'Insider',
                    'risk': 'Risk',
                    'sector': 'Sector',
                    'screen': 'Screener',
                    'economic': 'Economic',
                  };
                  return (
                    <span key={toolId} className="keyword-chip">
                      {toolLabels[toolId]}
                      <button 
                        className="chip-remove"
                        onClick={() => setSelectedTools(selectedTools.filter(t => t !== toolId))}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
                <button 
                  className="clear-all-chips"
                  onClick={() => setSelectedTools([])}
                  title="Clear all tools"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          <div className="input-right-actions">
            <button 
              type="button" 
              className={`input-icon-btn voice-btn ${isRecording ? 'recording' : ''}`}
              onClick={toggleVoiceInput}
              title={isRecording ? 'Stop recording' : 'Voice input'}
            >
              {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
            
            {messages.length > 0 && (
              <button 
                type="button" 
                className="input-icon-btn"
                onClick={clearConversation}
                title="Clear conversation"
              >
                <Trash size={18} />
              </button>
            )}
            
            {messages.length > 0 && currentChatId && (
              <button 
                type="button" 
                className="input-icon-btn"
                onClick={() => shareChat(currentChatId, 'link')}
                title="Share chat"
              >
                <Share2 size={18} />
              </button>
            )}
            
            <button 
              type="button" 
              className={`send-btn ${(inputValue.trim() || selectedTools.length > 0) ? 'active' : ''}`}
              disabled={isLoading || (!inputValue.trim() && selectedTools.length === 0)}
              onClick={handleSubmit}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13"/>
                <path d="M22 2L15 22L11 13L2 9L22 2Z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisPage;